'use client';

import { ReactNode, useEffect, useRef, useState } from 'react';

import Image from 'next/image';

import {
  AnimatePresence,
  motion,
  useScroll,
  useTransform,
} from 'framer-motion';

import { CaseStudyHeroVideo } from '@/components/case-study/case-study-hero-video';
import { Button } from '@/components/ui/button';
import { SubtleEntrance } from '@/components/ui/subtle-entrance';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { spaceBreaks } from '@/lib/space-breaks';
import { cn } from '@/lib/utils';

export interface SubpageHeroIntegration {
  src: string;
  alt: string;
}

const DEFAULT_WORKS_WITH: SubpageHeroIntegration[] = [
  { src: '/helpdicks/zendesk.png', alt: 'Zendesk' },
  { src: '/helpdicks/hubspot.png', alt: 'HubSpot' },
  { src: '/helpdicks/intercom.png', alt: 'Intercom' },
  { src: '/helpdicks/freshdesk.png', alt: 'Freshdesk' },
  { src: '/helpdicks/salesforce.png', alt: 'Salesforce' },
  { src: '/helpdicks/flex.png', alt: 'Twilio Flex' },
];

export interface SubpageHeroBubble {
  role: 'user' | 'agent';
  /** Plain text content for the bubble (optional if `widget` is set) */
  text?: string;
  /** Optional rich UI rendered inside the bubble – e.g. time picker, quick replies */
  widget?: ReactNode;
  agentName?: string;
}

interface SubpageHeroProps {
  /** Small label above the title – e.g. "Product", "Channels" */
  eyebrow?: string;
  title: ReactNode;
  /** Supports a string or any node so callers can split the subtitle onto lines */
  subtitle: ReactNode;
  /** Optional CTA – hidden if `ctaText` is null */
  ctaText?: string | null;
  ctaUrl?: string;
  /** Path under /public to the background video (omit to hide the video card) */
  videoSrc?: string;
  /** CSS object-position override for the video (default: "center") */
  videoObjectPosition?: string;
  /** Makes the hero video playable, using the site's cinematic player —
      poster frame, one centred "Play with sound", then small hover controls.
      Off by default, so the 31 pages using this hero as silent background
      b-roll are untouched. */
  videoControls?: boolean;
  /** Seconds to seek to for the paused poster frame. */
  videoStartAt?: number;
  /** Aspect ratio of the visual card (default 21/9 – cinematic strip) */
  aspectRatio?: string;
  /** Optional chat bubbles overlaid on the video, animated in sequence */
  bubbles?: SubpageHeroBubble[];
  /** Default agent name displayed on agent bubbles when none is set per-bubble */
  bubbleAgentName?: string;
  /** Corner of the video the bubbles render in (default: "bottom-left") */
  bubblePosition?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  /**
   * Small "Works with" row of integration logos under the CTA.
   * Pass `null` to hide. Defaults to the major helpdesks.
   */
  worksWith?: SubpageHeroIntegration[] | null;
  /** Label shown before the integration logos (default "Works with") */
  worksWithLabel?: string;
}

/**
 * Sub-page hero (used by product / channel pages).
 *
 * Clean two-block layout:
 *   1. Tight left-aligned text block (eyebrow + title + subtitle + optional CTA)
 *   2. Full-width rounded video card below
 *
 * Different on purpose from the home hero (which is a full-bleed cinematic
 * video with overlaid text) so users instantly read this as a "sub" page.
 */
export const SubpageHero = ({
  eyebrow,
  title,
  subtitle,
  ctaText,
  ctaUrl = '/demo',
  videoSrc,
  videoObjectPosition = 'center',
  videoControls = false,
  videoStartAt,
  aspectRatio = '21/9',
  bubbles,
  bubbleAgentName,
  bubblePosition = 'bottom-left',
  worksWith = DEFAULT_WORKS_WITH,
  worksWithLabel,
}: SubpageHeroProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const { t, locale, isRTL } = useTranslation();

  const showCta = ctaText !== null;
  const resolvedCta = ctaText ?? t.nav['Get a demo'] ?? 'Get a demo';
  const resolvedWorksWithLabel =
    worksWithLabel ?? (locale === 'ar' ? 'يشتغل مع' : 'Works with');
  const resolvedBubbleAgentName =
    bubbleAgentName ?? (locale === 'ar' ? 'الوكيل الذكي' : 'AI Agent');

  // Parallax: translate the video as the section scrolls through the viewport.
  // Section "enters" the bottom → progress 0. Section "leaves" the top → progress 1.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const videoY = useTransform(scrollYProgress, [0, 1], ['10%', '-10%']);

  return (
    <section
      ref={sectionRef}
      className={cn(
        'relative overflow-hidden bg-white pt-32 md:pt-40',
        videoSrc ? 'pb-12 md:pb-16' : 'pb-20 md:pb-28',
      )}
    >
      {/* Text block – constrained to the standard container width */}
      <div className="container">
        {/* The bottom margin only exists to separate this text from the
            video card below it. With no videoSrc there is no card, and the
            margin was stacking on top of the section's own (already larger)
            bottom padding as dead space. */}
        <div className={cn('max-w-3xl', videoSrc && 'mb-10 md:mb-14')}>
          {eyebrow && (
            <SubtleEntrance animateOnMount className="block">
              <p className="mb-4 text-sm font-medium text-neutral-500">
                {eyebrow}
              </p>
            </SubtleEntrance>
          )}

          <SubtleEntrance animateOnMount delay={0.05}>
            <h1
              className={cn(
                'font-serif text-balance text-neutral-900',
                'text-[2rem] md:text-4xl xl:text-5xl',
              )}
            >
              {spaceBreaks(title)}
            </h1>
          </SubtleEntrance>

          <SubtleEntrance animateOnMount delay={0.12}>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
              {spaceBreaks(subtitle)}
            </p>
          </SubtleEntrance>

          {showCta && (
            <SubtleEntrance animateOnMount delay={0.18}>
              <div className="mt-8">
                <Button
                  asChild
                  className="h-11 rounded-full bg-neutral-900 px-6 text-sm font-medium text-white hover:bg-neutral-800"
                >
                  <Link
                    href={ctaUrl}
                    prefetch={true}
                    aria-label={
                      ctaUrl === '/demo'
                        ? t.common['Learn more about Open and book a demo']
                        : undefined
                    }
                  >
                    {resolvedCta}
                  </Link>
                </Button>
              </div>
            </SubtleEntrance>
          )}

          {worksWith && worksWith.length > 0 && (
            <SubtleEntrance animateOnMount delay={0.24}>
              <div className="mt-5 flex items-center gap-3">
                <span className="text-xs font-medium text-neutral-500">
                  {resolvedWorksWithLabel}
                </span>
                <TooltipProvider delayDuration={120} skipDelayDuration={80}>
                  <div className="flex">
                    {worksWith.map((item, index) => (
                      <Tooltip key={item.src}>
                        <TooltipTrigger asChild>
                          <div
                            className={cn(
                              'relative flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 bg-white transition-all duration-200 hover:z-10 hover:scale-110',
                              index > 0 && 'ltr:-ml-2 rtl:-mr-2',
                            )}
                          >
                            <Image
                              src={item.src}
                              alt={item.alt}
                              width={20}
                              height={20}
                              className="h-4 w-4 object-contain"
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="bg-neutral-900 text-white"
                        >
                          {item.alt}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </TooltipProvider>
              </div>
            </SubtleEntrance>
          )}
        </div>
      </div>

      {/* Visual card – breaks out beyond the text container */}
      {videoSrc && (
        <SubtleEntrance animateOnMount delay={0.22}>
          <div className="mx-auto max-w-[1440px] px-4 sm:px-6 md:px-8">
            {videoControls ? (
              /* The site has one playable-video treatment and this is it:
                 poster frame, a single centred "Play with sound", then small
                 hover controls. No native control bar — nothing on the site
                 renders one. It brings its own card chrome, so it replaces
                 the card below rather than sitting inside it. */
              <CaseStudyHeroVideo
                src={videoSrc}
                aspectRatio={aspectRatio}
                startAt={videoStartAt}
                variant="cinematic"
              />
            ) : (
              <div
                className="relative w-full overflow-hidden rounded-[2.5rem] bg-neutral-100 ring-1 ring-neutral-900/5 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.25)] md:rounded-[3rem]"
                style={{ aspectRatio }}
              >
                {/* Parallax wrapper – the video sits 20% above/below the card
                  so the translateY (driven by scroll) never reveals an empty edge. */}
                <motion.div
                  style={{ y: videoY, top: '-20%', bottom: '-20%' }}
                  className="absolute inset-x-0 motion-reduce:!translate-y-0"
                >
                  <video
                    src={videoSrc}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-hidden="true"
                    className="block h-full w-full object-cover"
                    style={{
                      objectPosition: videoObjectPosition,
                      transform: isRTL ? 'scaleX(-1)' : undefined,
                    }}
                  />
                </motion.div>

                {bubbles && bubbles.length > 0 && (
                  <BubbleOverlay
                    bubbles={bubbles}
                    agentName={resolvedBubbleAgentName}
                    position={bubblePosition}
                  />
                )}
              </div>
            )}
          </div>
        </SubtleEntrance>
      )}
    </section>
  );
};

interface ActiveBubble extends SubpageHeroBubble {
  id: number;
}

export const BubbleOverlay = ({
  bubbles,
  agentName,
  position = 'bottom-left',
}: {
  bubbles: SubpageHeroBubble[];
  agentName?: string;
  position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
}) => {
  const { locale } = useTranslation();
  const resolvedAgentName =
    agentName ?? (locale === 'ar' ? 'وكيل Open' : 'Open Agent');
  const [active, setActive] = useState<ActiveBubble[]>([]);
  const indexRef = useRef(0);
  const idRef = useRef(0);

  useEffect(() => {
    if (bubbles.length === 0) return;

    // Single bubble: show it once, no cycling, no auto-removal.
    if (bubbles.length === 1) {
      setActive([{ ...bubbles[0], id: idRef.current++ }]);
      return;
    }

    // Multiple bubbles: cycle every ~3s, keep only the most recent few visible
    const tick = () => {
      const source = bubbles[indexRef.current % bubbles.length];
      indexRef.current += 1;
      const next: ActiveBubble = { ...source, id: idRef.current++ };
      setActive((prev) => [...prev, next].slice(-3));
    };
    tick();
    const interval = setInterval(tick, 3000);
    return () => clearInterval(interval);
  }, [bubbles]);

  return (
    <div
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-x-0 z-10 flex flex-col gap-2.5 px-4 sm:px-6 md:gap-3 md:px-8',
        position.startsWith('top')
          ? 'top-0 pt-4 sm:pt-6 md:pt-8'
          : 'bottom-0 pb-4 sm:pb-6 md:pb-8',
        position.endsWith('right') ? 'items-end' : 'items-start',
      )}
    >
      <AnimatePresence initial={false}>
        {active.map((bubble, index) => {
          // Only two bubbles are shown in full; the oldest visible (top) fades.
          const isFading = active.length >= 3 && index === 0;
          return (
            <motion.div
              key={bubble.id}
              layout
              initial={{ opacity: 0, y: 16, filter: 'blur(10px)' }}
              animate={{
                opacity: isFading ? 0.55 : 1,
                y: 0,
                filter: 'blur(0px)',
              }}
              exit={{ opacity: 0, y: -10, filter: 'blur(8px)' }}
              transition={{
                duration: 0.85,
                ease: [0.22, 1, 0.36, 1],
                layout: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
              }}
              className={cn(
                'max-w-[88%] border text-sm text-white shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:max-w-[68%] md:max-w-[50%] md:text-[15px]',
                bubble.text ? 'rounded-2xl px-3.5 py-2.5' : 'rounded-xl p-1.5',
                bubble.role === 'agent'
                  ? 'border-white/15 bg-white/15'
                  : 'border-white/10 bg-neutral-900/30',
              )}
            >
              {bubble.role === 'agent' && bubble.text && (
                <p className="mb-1 text-[11px] font-medium tracking-wide text-white/70">
                  {bubble.agentName ?? resolvedAgentName}
                </p>
              )}
              {bubble.text && <p className="leading-snug">{bubble.text}</p>}
              {bubble.widget && (
                <div className={cn(bubble.text ? 'mt-2.5' : 'mt-0')}>
                  {bubble.widget}
                </div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

/**
 * Reusable widgets that can be dropped into a bubble's `widget` slot.
 * They convey that Open's AI can reply with real interactive UI, not just text.
 */

interface BubbleTimeSlotsProps {
  day: string;
  slots: string[];
  selected?: string;
  confirmLabel?: string;
}

export const BubbleTimeSlots = ({
  day,
  slots,
  selected,
  confirmLabel,
}: BubbleTimeSlotsProps) => {
  const { locale } = useTranslation();
  const resolvedConfirmLabel =
    confirmLabel ?? (locale === 'ar' ? 'أكّد' : 'Confirm');
  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.06]">
      <div className="flex items-center justify-between px-3 py-1.5 text-[11px] font-medium text-white/80">
        <span className="text-white/40">‹</span>
        <span className="tracking-wide">{day}</span>
        <span className="text-white/40">›</span>
      </div>
      <div className="flex gap-1.5 border-t border-white/10 px-2.5 py-2">
        {slots.map((slot) => (
          <span
            key={slot}
            className={cn(
              'rounded-full px-2 py-1 text-[11px] tabular-nums',
              slot === selected
                ? 'bg-amber-300/90 text-neutral-900'
                : 'text-white/80',
            )}
          >
            {slot}
          </span>
        ))}
      </div>
      <div className="border-t border-white/10 px-2.5 pb-2 pt-2">
        <div className="w-full rounded-md bg-white px-3 py-1.5 text-center text-[12px] font-medium text-neutral-900">
          {resolvedConfirmLabel}
        </div>
      </div>
    </div>
  );
};

interface BubbleQuickRepliesProps {
  options: string[];
  selected?: string;
}

export const BubbleQuickReplies = ({
  options,
  selected,
}: BubbleQuickRepliesProps) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((option) => (
      <span
        key={option}
        className={cn(
          'rounded-full border px-3 py-1 text-[12px] font-medium',
          option === selected
            ? 'border-white/40 bg-white text-neutral-900'
            : 'border-white/20 bg-white/10 text-white/85',
        )}
      >
        {option}
      </span>
    ))}
  </div>
);

interface BubbleEmailDraftProps {
  to: string;
  body: string;
  primaryLabel?: string;
}

export const BubbleEmailDraft = ({
  to,
  body,
  primaryLabel,
}: BubbleEmailDraftProps) => {
  const { locale } = useTranslation();
  const isAr = locale === 'ar';
  const resolvedPrimaryLabel = primaryLabel ?? (isAr ? 'أرسل' : 'Send');
  return (
    <div className="space-y-1.5 rounded-lg bg-white/[0.06] p-3">
      <div className="text-[11px] text-white/55">
        <span className="text-white/40">{isAr ? 'إلى ' : 'To '}</span>
        <span className="text-white/80">{to}</span>
      </div>
      <p className="text-[12.5px] leading-snug text-white/90">{body}</p>
      <div>
        <span className="inline-block rounded-md bg-white px-2.5 py-1 text-[11px] font-medium text-neutral-900">
          {resolvedPrimaryLabel}
        </span>
      </div>
    </div>
  );
};

interface BubbleAutopilotProps {
  title?: string;
  steps: string[];
  /** ms between each step completing (default 1100) */
  intervalMs?: number;
}

/**
 * Renders a small "workflow runner" card where steps complete one-by-one,
 * giving the impression of automation running on autopilot.
 */
export const BubbleAutopilot = ({
  title,
  steps,
  intervalMs = 1100,
}: BubbleAutopilotProps) => {
  const { locale } = useTranslation();
  const resolvedTitle =
    title ??
    (locale === 'ar' ? 'Autopilot · قيد التشغيل' : 'Autopilot · running');
  const [completed, setCompleted] = useState(0);

  useEffect(() => {
    if (steps.length === 0) return;
    const id = setInterval(() => {
      setCompleted((c) => (c >= steps.length ? 0 : c + 1));
    }, intervalMs);
    return () => clearInterval(id);
  }, [steps.length, intervalMs]);

  return (
    <div className="space-y-2 rounded-lg bg-white/[0.06] p-3">
      <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-white/55">
        <span className="relative inline-flex h-1.5 w-1.5">
          <span className="absolute inset-0 animate-ping rounded-full bg-emerald-300/70" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-300" />
        </span>
        {resolvedTitle}
      </div>
      <div className="space-y-1.5">
        {steps.map((step, i) => {
          const isDone = i < completed;
          const isActive = i === completed && completed < steps.length;
          return (
            <div
              key={`${step}-${i}`}
              className={cn(
                'flex items-center gap-2 text-[12.5px] transition-all duration-300',
                isDone
                  ? 'text-white/85'
                  : isActive
                    ? 'text-white'
                    : 'text-white/30',
              )}
            >
              <span
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold transition-all duration-300',
                  isDone
                    ? 'bg-emerald-300 text-neutral-900'
                    : isActive
                      ? 'bg-white/15 text-white animate-pulse'
                      : 'bg-white/5 text-transparent',
                )}
              >
                {isDone ? '✓' : isActive ? '·' : ''}
              </span>
              <span className="leading-snug">{step}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface BubbleInboundProps {
  /** Source helpdesk / channel name (e.g. "Intercom") */
  source: string;
  /** Path to the source's brand logo */
  sourceLogo: string;
  /** Ticket title / subject */
  title: string;
  /** First-line preview of the message body */
  preview: string;
}

/**
 * Renders an inbound ticket arriving from a helpdesk (with that helpdesk's
 * logo) – pairs naturally with `BubbleAutopilot` to show Open resolving it.
 */
export const BubbleInbound = ({
  source,
  sourceLogo,
  title,
  preview,
}: BubbleInboundProps) => {
  const { locale } = useTranslation();
  const inboundLabel = locale === 'ar' ? 'وارد' : 'Inbound';
  return (
    <div className="space-y-2 rounded-lg bg-white/[0.06] p-3">
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white/10 p-1">
          <Image
            src={sourceLogo}
            alt={source}
            width={20}
            height={20}
            className="h-full w-auto object-contain"
          />
        </div>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="text-[10px] font-medium uppercase tracking-wider text-white/55">
            {inboundLabel} · {source}
          </div>
          <div className="truncate text-[12.5px] font-medium text-white">
            {spaceBreaks(title)}
          </div>
        </div>
      </div>
      <p className="rounded-md border border-white/10 bg-black/15 px-2.5 py-1.5 text-[12px] leading-snug text-white/85">
        “{preview}”
      </p>
    </div>
  );
};

interface BubbleAttachmentProps {
  fileName: string;
  fileType?: string;
  insight: string;
}

export const BubbleAttachment = ({
  fileName,
  fileType = 'PDF',
  insight,
}: BubbleAttachmentProps) => (
  <div className="flex items-center gap-2.5 rounded-lg bg-white/[0.06] p-2.5">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-300/20 text-[10px] font-bold tracking-wide text-amber-200">
      {fileType}
    </div>
    <div className="min-w-0 flex-1 leading-tight">
      <div className="truncate text-[12.5px] font-medium text-white">
        {fileName}
      </div>
      <div className="truncate text-[11px] text-white/65">{insight}</div>
    </div>
  </div>
);

interface BubbleCallProps {
  /** "live" → active call (amber dot), "resolved" → check mark */
  status: 'live' | 'resolved';
  caller: string;
  /**
   * Call duration label. If omitted while status is "live", the bubble
   * shows a self-ticking mm:ss timer.
   */
  duration?: string;
  /** What the call is about, e.g. "Refund inquiry · resolved" */
  topic: string;
}

const padTwo = (n: number) => n.toString().padStart(2, '0');
const formatCallDuration = (totalSec: number) =>
  `${padTwo(Math.floor(totalSec / 60))}:${padTwo(totalSec % 60)}`;

export const BubbleCall = ({
  status,
  caller,
  duration,
  topic,
}: BubbleCallProps) => {
  const [seconds, setSeconds] = useState(0);
  const shouldTick = status === 'live' && !duration;

  useEffect(() => {
    if (!shouldTick) return;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [shouldTick]);

  const displayDuration = duration ?? formatCallDuration(seconds);

  return (
    <div className="flex items-center gap-3 rounded-lg bg-white/[0.06] p-2.5">
      <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10">
        {status === 'live' ? (
          <span className="relative h-2.5 w-2.5 rounded-full bg-amber-300" />
        ) : (
          <span className="text-[13px] font-bold text-white/85">✓</span>
        )}
      </div>
      <div className="min-w-0 flex-1 leading-tight">
        <div className="flex items-baseline gap-1.5">
          <span className="truncate text-[12.5px] font-medium text-white">
            {caller}
          </span>
          <span className="font-mono text-[10.5px] tabular-nums text-white/50">
            {displayDuration}
          </span>
        </div>
        <div className="truncate text-[11px] text-white/65">{topic}</div>
      </div>
    </div>
  );
};

export default SubpageHero;
